import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { InputSearchComponent } from '@components/form-controls/inputs/input-search/input-search.component';
import { Observable, Subscription, timer } from 'rxjs';
import { TreeNode } from '../../../models/structures/tree';
import { TreeBuilder } from './tree-builder';

export interface ComponentTreeNode<T = any> extends TreeNode<TreeNodeData<T>> {}
export interface TreeNodeData<T = any> {
  level: number;
  title: string;
  item: T;
  icon: string;
  enabled?: boolean;
  addButton?: boolean;
  routerLink?: string;
}

export interface FlatTreeNode<T = any> extends TreeNodeData<T> {
  treeNode: TreeNode<T>;
}

export enum TreeMode {
  DEFAULT = 'default',
  SMALL = 'small'
}

@Component({
  selector: 'vfs-tree',
  templateUrl: './tree.component.html',
  styleUrls: ['./tree.component.scss'],
  providers: [TreeBuilder],
  encapsulation: ViewEncapsulation.None
})
export class TreeComponent implements OnInit, OnDestroy {
  @Input() keepExpandedNodes = false;
  @Input() treeData: Observable<ComponentTreeNode[]>;
  treeDataSubscription$: Subscription;
  @Input() mode: TreeMode = TreeMode.DEFAULT;
  @Input() header = true; // Show searchbox and expand button
  @Input() checkBox = false; // Active checkbox
  @Input() addButton = false; // Active add node button
  @Input() noPadding = false;
  @Input() addButtonTooltip: string;

  @Output() nodeInfo = new EventEmitter(); // Emit node info
  @Output() addEvent = new EventEmitter(); // Emit add button event

  @ViewChild('searchBox') searchBox: InputSearchComponent;
  loading = false;
  expanded = false;
  activeNode: FlatTreeNode;

  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<FlatTreeNode, ComponentTreeNode>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<ComponentTreeNode, FlatTreeNode>();

  /** A selected parent node to be inserted */
  selectedParent: FlatTreeNode | null = null;

  treeControl: FlatTreeControl<FlatTreeNode>;

  treeFlattener: MatTreeFlattener<ComponentTreeNode, FlatTreeNode>;

  dataSource: MatTreeFlatDataSource<ComponentTreeNode, FlatTreeNode>;

  /** The selection for checklist */
  checklistSelection = new SelectionModel<FlatTreeNode>(true /* multiple */);

  constructor(private treeBuilder: TreeBuilder) {
    this.treeFlattener = new MatTreeFlattener(
      (node: ComponentTreeNode) => this.transformer(node),
      this.getLevel,
      this.isExpandable,
      this.getChildren
    );
    this.treeControl = new FlatTreeControl<FlatTreeNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    this.treeBuilder.dataChange.subscribe(data => {
      this.dataSource.data = data;
      if (this.checkBox) {
        this.checkSelectedNodes();
      }
    });
  }

  expandedNodes: FlatTreeNode<any>[] = [];

  ngOnInit() {
    this.loading = true;
    this.treeDataSubscription$ = this.treeData.subscribe(data => {
      if (this.keepExpandedNodes) {
        this.saveExpandedNodes();
      }
      this.treeBuilder.initialize(data);
      if (this.keepExpandedNodes) {
        this.restoreExpandedNodes();
      }
      this.setSearchBoxValue();
      this.loading = false;
    });
  }

  saveExpandedNodes() {
    this.expandedNodes = new Array<FlatTreeNode<any>>();
    this.treeControl.dataNodes.forEach(node => {
      if (this.isExpandable(node) && this.treeControl.isExpanded(node)) {
        this.expandedNodes.push(node);
      }
    });
  }

  // restoreExpandedNodes() {
  //   this.expandedNodes.forEach(node => {
  //     this.treeControl.expand(this.treeControl.dataNodes.find(n => n.treeNode.getId() === node.treeNode.getId()));
  //   });
  // }

  restoreExpandedNodes() {
    this.expandedNodes.forEach(node => {
      this.treeControl.expand(this.treeControl.dataNodes.find(n => this.compareNodeId(n.treeNode, node.treeNode)));
    });
  }

  compareNodeId(n1: TreeNode, n2: TreeNode): boolean {
    return n1.getId() != null && n1.getId() === n2.getId();
  }

  /**
   * Iterate the tree to check all enabled nodes.
   */
  checkSelectedNodes() {
    if (this.flatNodeMap && this.treeData) {
      for (let node of this.flatNodeMap.keys()) {
        // TODO: why only the 2nd level?
        if (node.level === 2 && node.enabled) {
          this.checklistSelection.select(node);
          this.checkAllParentsSelection(node);
        }
      }
    }
  }

  /**
   * Clean tree data
   */
  ngOnDestroy(): void {
    if (this.treeDataSubscription$ != null) {
      this.treeDataSubscription$.unsubscribe();
    }
  }

  getLevel(node: FlatTreeNode) {
    return node.level;
  }

  isExpandable(node: FlatTreeNode) {
    return node.treeNode.getChildren().length > 0;
  }

  hasChild(_: number, node: FlatTreeNode) {
    return node.treeNode.getChildren().length > 0;
  }

  hasNoContent(_: number, node: FlatTreeNode) {
    return node.treeNode.getData().title === '';
  }

  getChildren(node: ComponentTreeNode): ComponentTreeNode[] {
    return node.getChildren();
  }

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer(node: ComponentTreeNode): FlatTreeNode {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode: FlatTreeNode =
      existingNode && existingNode.title === node.getData().title ? existingNode : ({} as FlatTreeNode);

    flatNode.treeNode = node;
    Object.assign(flatNode, node.getData());

    // TODO: check this enabled
    flatNode.enabled = true;

    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  }

  /**
   * Whether all the descendants of the node are selected.
   * @param node The tree node
   */
  descendantsAllSelected(node: FlatTreeNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.every(child => this.checklistSelection.isSelected(child));
    return descAllSelected;
  }

  /**
   * Whether part of the descendants are selected.
   * @param node The tree node
   */
  descendantsPartiallySelected(node: FlatTreeNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some(child => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  /**
   * Toggle the to-do item selection. Select/deselect all the descendants node
   * @param node The tree node
   */
  todoItemSelectionToggle(node: FlatTreeNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);

    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);

    let updateDesc = descendants.every(child => this.checklistSelection.isSelected(child));
    this.checkAllParentsSelection(node);
  }

  /**
   * Toggle a leaf to-do item selection. Check all the parents to see if they changed.
   * @param node The tree node
   */
  todoLeafItemSelectionToggle(node: FlatTreeNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
  }

  /**
   * Checks all the parents when a leaf node is selected/unselected.
   * @param node The tree node
   */
  checkAllParentsSelection(node: FlatTreeNode): void {
    let parent: FlatTreeNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  /**
   * Check root node checked state and change it accordingly
   * @param node The tree node
   */
  checkRootNodeSelection(node: FlatTreeNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.every(child => this.checklistSelection.isSelected(child));
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  /**
   * Get the parent node of a node
   * @param node The tree node
   */
  getParentNode(node: FlatTreeNode): FlatTreeNode | null {
    let treeNodeParent = node.treeNode.getParent();
    if (treeNodeParent != null) {
      return this.nestedNodeMap.get(treeNodeParent);
    }
    return null;
  }

  /**
   * Filter the tree
   * @param filterText Text to filter
   */
  filterChanged(filterText: string) {
    this.treeBuilder.filter(filterText);
    if (filterText) {
      this.loading = true;
      // TODO: not sure about this expand (maybe expand just those that matches)
      timer(0).subscribe(_ => {
        this.treeControl.expandAll();
        this.loading = false;
      });
      this.expanded = true;
    } else {
      // TODO: not sure about this collapse (maybe collapse just those without matching children)
      this.treeControl.collapseAll();
      this.expanded = false;
    }
  }

  expandTree() {
    if (this.expanded) {
      this.treeControl.collapseAll();
      this.expanded = false;
    } else {
      this.loading = true;
      timer(0).subscribe(_ => {
        this.treeControl.expandAll();
        this.loading = false;
      });
      this.expanded = true;
    }
  }

  nodeClick(node: FlatTreeNode) {
    this.activeNode = node;
    this.nodeInfo.emit(node);
  }

  addClick(node: FlatTreeNode, event?) {
    this.addEvent.emit(node);
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  setSearchBoxValue() {
    let searchValue = this.searchBox.inputRef.nativeElement.value;
    if (searchValue) {
      this.filterChanged(searchValue);
    }
  }
}
